import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { RateLimitService } from './rate-limit.service';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/finally';

@Injectable()
export class RequestQueueService {
  private queue$ = new Subject();
  private processing = false;
  private requestsPerSecond = 10;
  private queuedRequests = 0;

  constructor(private rateLimitService: RateLimitService) {
    this.queue$
      .concatMap((request: { request: Observable<any>; subject: Subject<any> }) => {
        if (!this.processing) {
          this.processing = true;
          return this.processRequest(request);
        }
        
        this.queuedRequests++;
        return Observable.of(null)
          .delay(Math.ceil(1000 / this.requestsPerSecond * this.queuedRequests))
          .flatMap(() => this.processRequest(request));
      })
      .subscribe(
        null,
        error => {
          // Prevent queue from breaking by catching errors at subscription level
          console.error('Queue processing error:', error);
          // Restart queue processing
          this.processing = false;
          this.queuedRequests = 0;
        }
      );
  }

  enqueue<T>(request: Observable<T>): Observable<T> {
    const subject = new Subject<T>();
    this.queue$.next({ request, subject });
    return subject.asObservable();
  }

  private processRequest({ request, subject }): Observable<any> {
    return request
      .do(
        response => {
          // Update rate limit info from response headers
          if (response && response.headers) {
            this.rateLimitService.updateRateLimitInfo(response.headers);
          }
          subject.next(response);
          subject.complete();
        },
        error => {
          // Handle rate limit errors through the rate limit service
          if (error.status === 429) {
            console.warn('Rate limit exceeded (429):', error);
            if (error.headers) {
              this.rateLimitService.updateRateLimitInfo(error.headers);
            }
          }
          
          // For all other errors, pass through to the subject
          subject.error(error);
        }
      )
      .catch(error => {
        // Ensure the queue continues processing even after an error
        console.warn('Request failed, continuing queue processing:', error);
        
        // Return empty observable to continue the queue
        return Observable.of(null);
      })
      .finally(() => {
        // Always ensure processing flag gets reset and queue count is decremented
        this.processing = false;
        this.queuedRequests = Math.max(0, this.queuedRequests - 1);
      });
  }
}


// WEBPACK FOOTER //
// ./src/app/core/services/request-queue.service.ts